import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { Capacitor } from '@capacitor/core';

/**
 * Haptic feedback utility that wraps @capacitor/haptics.
 * Silently fails on web or if haptics are not available.
 */
export const haptics = {
  /**
   * Trigger impact haptic feedback.
   * @param style - Impact intensity (Light, Medium, Heavy)
   */
  impact: async (style: ImpactStyle = ImpactStyle.Medium) => {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style });
      } catch {
        // Silently fail if haptics not available
      }
    }
  },

  /**
   * Trigger notification haptic feedback.
   * @param type - Notification type (Success, Warning, Error)
   */
  notification: async (type: NotificationType = NotificationType.Success) => {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.notification({ type });
      } catch {
        // Silently fail
      }
    }
  },

  /**
   * Trigger selection changed haptic feedback.
   */
  selection: async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.selectionStart();
        await Haptics.selectionEnd();
      } catch {
        // Silently fail
      }
    }
  },

  /**
   * Trigger a "spring" haptic pattern - a quick light-medium sequence.
   * Great for button taps and micro-interactions.
   */
  spring: async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style: ImpactStyle.Light });
        await new Promise((resolve) => setTimeout(resolve, 50));
        await Haptics.impact({ style: ImpactStyle.Medium });
      } catch {
        // Silently fail
      }
    }
  },

  /**
   * Trigger a "celebrate" haptic pattern - success notification with impacts.
   * Perfect for achievements, completions, and positive feedback.
   */
  celebrate: async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.notification({ type: NotificationType.Success });
        await new Promise((resolve) => setTimeout(resolve, 100));
        await Haptics.impact({ style: ImpactStyle.Heavy });
      } catch {
        // Silently fail
      }
    }
  },

  /**
   * Trigger a "double tap" haptic pattern - two quick light impacts.
   * Good for toggles, likes, and quick actions.
   */
  doubleTap: async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style: ImpactStyle.Light });
        await new Promise((resolve) => setTimeout(resolve, 80));
        await Haptics.impact({ style: ImpactStyle.Light });
      } catch {
        // Silently fail
      }
    }
  },

  /**
   * Trigger a "warning" haptic pattern.
   * Use for confirmations, destructive actions, or alerts.
   */
  warning: async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.notification({ type: NotificationType.Warning });
      } catch {
        // Silently fail
      }
    }
  },

  /**
   * Trigger an "error" haptic pattern.
   * Use for failed actions or validation errors.
   */
  error: async () => {
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.notification({ type: NotificationType.Error });
      } catch {
        // Silently fail
      }
    }
  },
};

// Re-export types for convenience
export { ImpactStyle, NotificationType };
